import React, { useMemo, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import _ from "lodash";
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Chip from '@material-ui/core/Chip';
import { FaTimes, FaPlus, FaMinus } from "react-icons/fa";
import DiscoverDropDownBox from "../../shared/Fields/DropDownBox";
import { useSelector } from 'react-redux';
import UtilsService from '../../helpers/utils';
import TextInput from '../../shared/Fields/TextInput';
import { famViewOptions, pnViewOptions } from '../../components/Discover/groupingOptions';

const useStyles = makeStyles((theme) => ({
    // As wide as the rows inside it, so the dialog stays centred and widens only when a row needs it
    // (a Query Sequence ID box, a second condition, Patent Family's two "show" choices)
    modalDialog: {
        '&.modal-dialog': {
            width: 'fit-content',
            maxWidth: 'none',
        },
    },
    modalClassConDSI: {
        color: '#777',
    },
    modalClassConDSIDark: {
        color: 'white',
        backgroundColor: '#292929',
        // The +/- buttons keep their light grey box, so their icons would inherit white; grey like Cancel
        '& .plus-min-button': {
            color: '#777',
        },
    },
    modalBoxContent: {
        maxHeight: '600px',
    },
    closeButton: {
        position: 'absolute',
        top: '10px',
        right: '10px'
    },
    inputLight:{
    },
    inputDark:{
        color: 'white',
        background: '#292929',
    },
    filterRow: {
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-start',
        marginBottom: '10px',
        // Line every control up on the first line: drop the dropdowns' own offsets, give the fields the
        // dropdowns' height, and let .control-spacer's 5px centre the +/- buttons on it
        '& .MuiFormControl-root': {
            margin: 0,
            padding: 0,
        },
        '& .MuiOutlinedInput-root': {
            minHeight: '43px',
        },
        '& .filter-widget-adddel': {
            paddingTop: 0,
        },
        // As wide as the dropdowns beside it rather than the rest of the row
        '& $queryIdInput': {
            width: '250px',
        },
    },
    queryIdInput: {},
    showRow: {
        // The global 12px input padding hides the arrow room; the "per Patent Number" labels need it
        '& .MuiSelect-select.MuiOutlinedInput-input': {
            paddingRight: '32px !important',
        },
        // Every choice takes its label's width, so Patent Family's two "show" choices fit on the line
        '& $groupBySelect': {
            minWidth: '150px',
        },
        '& $showSelect': {
            minWidth: 0,
        },
    },
    groupBySelect: {},
    showSelect: {},
    // The Query Sequence ID list opens in a portal outside the dialog, so it needs its own dark colours
    queryIdPaperDark: {
        backgroundColor: '#292929',
        color: 'white',
        border: '1px solid grey',
        '& .MuiAutocomplete-option[data-focus="true"]': {
            backgroundColor: '#444',
        },
    },
}));

const toolTipStyles = {
    tooltip: {
        fontSize: '12px',
        textAlign: 'justify',
        padding: "8px 14px 8px 14px",
        boxShadow: "0px 1px 7px #888",
        backgroundColor: '#777',
        color: '#FFF'
    }
};
const CustomTooltip = withStyles(toolTipStyles)(Tooltip);

const GROUP_SIZE_FILTER = 'groupSize';
const QUERY_SEQ_ID_FILTER = 'querySeqId';

const DEFAULT_MATCH_TYPE = {
    [GROUP_SIZE_FILTER]: 'LESS_NE',
    [QUERY_SEQ_ID_FILTER]: 'ONE', // "every member matches" is never true when a group holds several query sequences
};

const viewOptions = [
    {value:'1', label:'1 Result'},
    {value:'3', label:'3 Results'},
    {value:'5', label:'5 Results'},
    {value:'10', label:'10 Results'},
    {value:'All', label:'All'}
];

// Groupings shared with the main Grouping menu, with the fieldConfig fields that hold their "show"
// choices there and the defaults Discover falls back to
const MENU_GROUPINGS = {
    document: {label: 'Patent Number', show: [{field: 'pnViewOption', items: pnViewOptions, fallback: '10'}]},
    family:   {label: 'Patent Family', show: [{field: 'famViewOption', items: famViewOptions, fallback: '3'},
                                              {field: 'famViewAliOption', items: pnViewOptions, fallback: '10'}]},
};

// The groupings only this dialog offers share one "N results per group" choice
const ADVANCED_GROUPINGS = [
    {value:'ali', label:'Alignment'},
    {value:'sid', label:'Subject Sequence'},
    {value:'gname', label: 'Subj. Database Name'},
    {value:'npa', label:'Normalized Patent Assignee'},
    // {value:'aipa', label:'Normalized Parent Assignee'}, // on hold for next release
    {value:'org', label:'Normalized Organism'},
    // {value:'gqid', label: 'GQ Identifier'}, // hidden for now
    {value:'extpf', label: 'Extended Patent Family'},
];
const ADVANCED_SHOW = [{field: 'groupOption', items: viewOptions, fallback: '10'}];

const showFieldsFor = (grouping) => MENU_GROUPINGS[grouping] ? MENU_GROUPINGS[grouping].show : ADVANCED_SHOW;

// Typed or pasted text may hold several IDs. Spaces stay inside an ID, as in the old one-per-line box;
// the backend already treats commas and semicolons as separators.
const QUERY_ID_SEPARATORS = /[\r\n\t,;]+/;
const splitQueryIds = (text) => String(text || '').split(QUERY_ID_SEPARATORS).map(id => id.trim()).filter(id => id !== '');
// A row's saved IDs, one per line; lists saved by older states may carry Windows line breaks
const splitLines = (text) => String(text || '').split(/\r?\n/).map(id => id.trim()).filter(id => id !== '');
const uniqueQueryIds = (ids) => _.uniqBy(ids, id => id.toUpperCase());
const sameQueryId = (a, b) => String(a).toUpperCase() === String(b).toUpperCase();

// Picks this result's query sequence IDs from a list. Any other ID can still be typed, and a trailing *
// matches by prefix. The row keeps the IDs as `value`, one per line as the backend expects, and the text
// being typed as `pending`, which Apply also counts: nothing turns into a chip when focus leaves the box,
// so the dialog does not shift under the Apply click. Clicking a chip puts its ID back in the box to edit.
// onChange receives only the fields that changed, so a value and a pending change in one event both land.
const QueryIdPicker = ({value, pending, options, onChange, className, inputClassName, paperClassName}) => {
    const inputRef = useRef(null);
    // A new array on every render would read as a new value, and Autocomplete clears what is being typed
    const values = useMemo(() => splitLines(value), [value]);
    const joined = (ids) => uniqueQueryIds(ids).join('\n');
    const editChip = (index) => {
        onChange({value: joined([...values.filter((id, i) => i !== index), ...splitQueryIds(pending)]), pending: values[index]});
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };
    return (
        <Autocomplete
            multiple
            freeSolo
            filterSelectedOptions
            className={className}
            classes={paperClassName ? {paper: paperClassName} : undefined}
            options={options}
            // Case-insensitive like the backend, so an ID typed in another case also hides its list entry
            getOptionSelected={sameQueryId}
            value={values}
            inputValue={pending}
            // Autocomplete's own resets would also wipe an ID put back for editing; pending is cleared below instead
            onInputChange={(e, text, reason) => reason !== 'reset' && onChange({pending: text})}
            onChange={(e, next, reason) => {
                if (reason === 'create-option') {
                    // Only typed text is split; an ID picked from the list is kept whole
                    onChange({value: joined([...next.slice(0, -1), ...splitQueryIds(next[next.length - 1])]), pending: ''});
                } else if (reason === 'remove-option') {
                    onChange({value: joined(next)});
                } else {
                    onChange({value: joined(next), pending: ''});
                }
            }}
            renderTags={(ids, getTagProps) => ids.map((id, index) => (
                <Chip {...getTagProps({index})} label={id} title="Click to edit" onClick={() => editChip(index)}/>
            ))}
            renderInput={(params) => (
                <TextInput {...params} variant="outlined" fullWidth={true} className={inputClassName} inputRef={inputRef}
                    label={values.length > 0 ? '' : 'Select or type Query Sequence IDs'}
                    inputProps={{
                        ...params.inputProps,
                        // An input drops a pasted list's line breaks, so take the list here: insert it where
                        // the caret is, as the box would, and turn the whole text into chips
                        onPaste: (e) => {
                            const text = e.clipboardData.getData('text');
                            if (!QUERY_ID_SEPARATORS.test(text.trim())) {
                                return;
                            }
                            e.preventDefault();
                            const start = e.target.selectionStart ?? pending.length;
                            const end = e.target.selectionEnd ?? pending.length;
                            const combined = pending.slice(0, start) + text + pending.slice(end);
                            onChange({value: joined([...values, ...splitQueryIds(combined)]), pending: ''});
                        },
                    }}/>
            )}
        />
    );
};

let rowKeySeq = 0;
const newRow = (type = GROUP_SIZE_FILTER, matchType, value = '') => ({
    key: ++rowKeySeq,
    type: type,
    matchType: matchType || DEFAULT_MATCH_TYPE[type],
    value: value,
    pending: '', // Query Sequence ID text typed but not yet turned into a chip
});

// Rebuilds the editor rows from what was last applied, so reopening the dialog does not lose the filter
const rowsFromFieldConfig = (fieldConfig) => {
    const rows = [];
    if (!fieldConfig) {
        return [newRow()];
    }
    if (!_.isEmpty(fieldConfig.groupFilters)) {
        fieldConfig.groupFilters.forEach(groupFilter => {
            rows.push(newRow(groupFilter.type, groupFilter.matchType, (groupFilter.values || []).join('\n')));
        });
    } else {
        // States saved before multiple rows existed carry a single condition in these three fields
        if (fieldConfig.groupFilterType === QUERY_SEQ_ID_FILTER && fieldConfig.groupFilterValue) {
            rows.push(newRow(QUERY_SEQ_ID_FILTER, fieldConfig.groupFilterMatchType, fieldConfig.groupFilterValue));
        }
        if (fieldConfig.groupSize) {
            rows.push(newRow(GROUP_SIZE_FILTER, fieldConfig.groupSizeOper, String(fieldConfig.groupSize)));
        }
    }
    return rows.length > 0 ? rows : [newRow()];
};

const AdvancedGroupModal = props => {

    const classes = useStyles();
    const groupOptions = [
        ...Object.keys(MENU_GROUPINGS).map(grouping => ({value: grouping, label: MENU_GROUPINGS[grouping].label})),
        ...ADVANCED_GROUPINGS,
    ];

    const filterTypeOptions = [
        {value: GROUP_SIZE_FILTER, label:'Group size'},
        {value: QUERY_SEQ_ID_FILTER, label:'Query Sequence ID'},
    ];

    const groupSizeOptions = [
        {value: "LESS_NE", label: "less than or equal to"},
        {value: "LESS", label: "less than"},
        {value: "EQUALS", label: "equals"},
        {value: "MORE", label: "greater than"},
        {value: "MORE_NE", label: "greater than or equal to"}
    ];

    const queryMatchOptions = [
        {value: "ONE", label: "one member matches"},
        {value: "EVERY", label: "every member matches"},
        {value: "NONE", label: "no member matches"},
    ];

    // The main Grouping menu has its own Subject view ('ssv') that this dialog does not list.
    // Treat it as the Subject option here, otherwise opening the dialog from that view would
    // silently switch the user to Alignment grouping.
    const currentGrouping = props.fieldConfig?.tableDisplayMode === 'ssv'
        ? 'sid' : props.fieldConfig?.tableDisplayMode;
    const initialGroupOption = groupOptions.some(o => o.value === currentGrouping) ? currentGrouping : 'ali';

    const [selectedGroupOption, setSelectedGroupOption] = useState(initialGroupOption);
    // Every grouping's "show" choices, so switching Group by back and forth keeps what was picked
    const [showValues, setShowValues] = useState(() => {
        const values = {};
        [...Object.values(MENU_GROUPINGS).flatMap(grouping => grouping.show), ...ADVANCED_SHOW].forEach(def => {
            values[def.field] = props.fieldConfig?.[def.field] || def.fallback;
        });
        return values;
    });
    const [filterRows, setFilterRows] = useState(() => rowsFromFieldConfig(props.fieldConfig));
    const toggle = useSelector(state => state.setToggle.toggle);
    const showFields = showFieldsFor(selectedGroupOption);

    const updateRow = (index, changes) => {
        setFilterRows(rows => rows.map((row, i) => i === index ? {...row, ...changes} : row));
    }

    const changeRowType = (index, type) => {
        // The operator list and the value differ per type, so start the row over
        updateRow(index, {type: type, matchType: DEFAULT_MATCH_TYPE[type], value: '', pending: ''});
    }

    const addRow = (index) => {
        setFilterRows(rows => {
            const next = [...rows];
            // Inherit the type of the row the + was clicked on, so adding next to a Group size
            // row gives another Group size row. Multiple rows of either type are AND'ed.
            next.splice(index + 1, 0, newRow(rows[index].type));
            return next;
        });
    }

    const removeRow = (index) => {
        setFilterRows(rows => rows.length > 1 ? rows.filter((row, i) => i !== index) : [newRow()]);
    }

    // Values within a row are OR'ed by the backend, rows are AND'ed. Empty rows are dropped.
    const buildGroupFilters = () => filterRows
        .map(row => ({
            type: row.type,
            matchType: row.matchType,
            values: row.type === GROUP_SIZE_FILTER
                ? [String(row.value).trim()].filter(value => value !== '')
                // Text still being typed counts too, so Apply does not need Enter first
                : uniqueQueryIds([...splitLines(row.value), ...splitQueryIds(row.pending)]),
        }))
        .filter(groupFilter => groupFilter.values.length > 0);

    const modalClose = () => {
        props.closeModal();
    }

    const modalSave = () => {
        const groupFilters = buildGroupFilters();
        // Mirror the first group size row onto the standalone fields the saved state has always used
        const sizeFilter = groupFilters.find(groupFilter => groupFilter.type === GROUP_SIZE_FILTER);
        const [option1 = '', option2 = ''] = showFields.map(def => showValues[def.field]);
        props.callback(props.fieldConfig, selectedGroupOption, option1, option2,
            sizeFilter ? sizeFilter.values[0] : '',
            sizeFilter ? sizeFilter.matchType : '',
            groupFilters);
    }

    return (
        <Modal
            aria-labelledby="contained-modal-title-vcente"
            centered
            show={props.showModal}
            dialogClassName={classes.modalDialog}
            contentClassName={toggle ? classes.modalClassConDSI: classes.modalClassConDSIDark }
        >
            {/* Equal side padding keeps the title centred and clear of the close icon */}
            <div className="really-large-font" style={{padding: '20px 50px 0', textAlign: 'center'}}>Advanced Group Options</div>
            <div className={classes.closeButton}>
                <FaTimes style={{ height: '2em', cursor: "pointer"}} onClick={modalClose} />
            </div>
            <div className="groupBig-message" >
                <div className="filter-widget-container">
                    <div className="rTable">
                        {/* Wraps only when Patent Family's two choices do not fit next to Group by */}
                        <div className={"rTableRow single-line-container " + classes.showRow} style={{flexWrap: 'wrap'}}>
                            <span >Group by</span>
                            <DiscoverDropDownBox
                                className={classes.groupBySelect}
                                margin="normal"
                                variant="outlined"
                                name="gro_opt"
                                id="gro_opt"
                                value={selectedGroupOption}
                                items={groupOptions}
                                onChange={(e) => setSelectedGroupOption(e.target.value)}
                            />
                            {showFields.length > 0 && (
                                <>
                                    <span>and show</span>
                                    {showFields.map(def => (
                                        <DiscoverDropDownBox
                                            key={def.field}
                                            className={classes.showSelect}
                                            margin="normal"
                                            variant="outlined"
                                            name={"show_" + def.field}
                                            id={"show_" + def.field}
                                            value={showValues[def.field]}
                                            items={def.items}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setShowValues(values => ({...values, [def.field]: value}));
                                            }}
                                        />
                                    ))}
                                    {/* The menu groupings' choices already name what they count per */}
                                    {showFields === ADVANCED_SHOW && <span>per group</span>}
                                </>
                            )}

                        </div>
                        <br></br>
                        <span >Show only groups with</span>

                        <div className="control-spacer">
                            {filterRows.map((row, index) => (
                                <div className={classes.filterRow} key={row.key}>
                                    <DiscoverDropDownBox
                                        margin="normal"
                                        variant="outlined"
                                        name={"filter_type_" + row.key}
                                        id={"filter_type_" + row.key}
                                        value={row.type}
                                        items={filterTypeOptions}
                                        onChange={(e) => changeRowType(index, e.target.value)}
                                    />
                                    <DiscoverDropDownBox
                                        margin="normal"
                                        variant="outlined"
                                        name={"filter_match_" + row.key}
                                        id={"filter_match_" + row.key}
                                        value={row.matchType}
                                        items={row.type === GROUP_SIZE_FILTER ? groupSizeOptions : queryMatchOptions}
                                        onChange={(e) => updateRow(index, {matchType: e.target.value})}
                                    />
                                    {row.type === GROUP_SIZE_FILTER ? (
                                        <TextInput fullWidth={false} type="number" className={toggle ? classes.inputLight : classes.inputDark}
                                                variant="outlined"
                                                InputProps={{ inputProps: { min: 0, max: 1000000 } }}
                                                value={row.value}
                                                onKeyDown={UtilsService.restrictCharacter}
                                                onChange={(e) => updateRow(index, {value: e.target.value})}/>
                                    ) : (
                                        <QueryIdPicker className={classes.queryIdInput}
                                                inputClassName={toggle ? classes.inputLight : classes.inputDark}
                                                paperClassName={toggle ? undefined : classes.queryIdPaperDark}
                                                options={uniqueQueryIds((props.queryIds || []).filter(id => id != null).map(String))}
                                                value={row.value}
                                                pending={row.pending || ''}
                                                onChange={(changes) => updateRow(index, changes)}/>
                                    )}
                                    {/* Only rendered with 2+ rows, when every row has one, so a single row
                                        does not keep an empty slot between its value and the + */}
                                    {filterRows.length > 1 &&
                                        <div className="filter-widget-adddel">
                                            <CustomTooltip arrow title="Remove this Condition" placement="bottom">
                                                <div className="control-spacer plus-min-button"
                                                     onClick={() => removeRow(index)}>
                                                    <FaMinus/>
                                                </div>
                                            </CustomTooltip>
                                        </div>
                                    }
                                    <div className="filter-widget-adddel">
                                        <CustomTooltip arrow title="Add Condition below" placement="bottom">
                                            <div className="control-spacer plus-min-button"
                                                 onClick={() => addRow(index)}>
                                                <FaPlus/>
                                            </div>
                                        </CustomTooltip>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* buttons */}
                        <div className="rTableRow">
                            <div className="rTableCell clearfix colspan">
                                {/* The shared 40px top padding leaves a wide gap under the conditions */}
                                <div className="clearfix filter-widget-bottom" style={{paddingTop: '10px'}}>
                                    <button className={'gqblue pull-right normal-button'} onClick={() => { modalSave() }}>Apply</button>
                                    <button className="pull-right normal-button grey" onClick={() => { props.closeModal() }}>Cancel</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

export default AdvancedGroupModal;
